import { FC, useEffect } from 'react';
import { graphql, useRefetchableFragment } from 'react-relay';
import { addLoginHandlers } from 'dibs-buyer-layout/exports/loginHandlers';
import { getBuyerId } from 'dibs-cookie-jar';

import serverVars from 'server-vars';
import classnames from 'classnames';
import { useLoggedInUser } from 'dibs-buyer-layout/exports/useLoggedInUser';
import { FormattedMessage } from 'dibs-react-intl';
import { shortAddress } from 'dibs-crypto-utils/exports/formattingUtils';
import { Button } from 'dibs-elements/exports/Button';
import { NftProfileCollection } from '../components/NftProfileCollection/NftProfileCollection';
import { NftPublicProfileAddressBadge } from '../components/NftPublicProfileAddressBadge/NftPublicProfileAddressBadge';
import { useHasMinterRole } from '../../hooks/useHasMinterRole';

import styles from './NftPublicProfileAddressRespLayout.scss';

import { NftPublicProfileAddressRespLayout_viewer$key } from './__generated__/NftPublicProfileAddressRespLayout_viewer.graphql';
import { NftPublicProfileAddressRespLayoutRefetchQuery } from './__generated__/NftPublicProfileAddressRespLayoutRefetchQuery.graphql';

type Props = {
    viewer: NftPublicProfileAddressRespLayout_viewer$key | null;
    walletAddress: string;
    ensDomain?: string;
    userId: string | null;
};

export const NftPublicProfileAddressRespLayout: FC<Props> = ({
    viewer: viewerRef,
    walletAddress,
    ensDomain,
    userId,
}) => {
    const isMobile = serverVars.get('settings.isMobile');
    const [viewer, refetch] = useRefetchableFragment<
        NftPublicProfileAddressRespLayoutRefetchQuery,
        NftPublicProfileAddressRespLayout_viewer$key
    >(
        graphql`
            fragment NftPublicProfileAddressRespLayout_viewer on Viewer
            @refetchable(queryName: "NftPublicProfileAddressRespLayoutRefetchQuery") {
                ...NftProfileCollection_viewer
                ...useHasMinterRole_viewer
            }
        `,
        viewerRef
    );
    const currentUserId = useLoggedInUser();
    const userHasMinterRole = useHasMinterRole({ viewer });

    useEffect(() => {
        const buyerId = getBuyerId(document.cookie);
        if (buyerId) {
            refetch({ userId: buyerId, fetchUser: true });
        }
        addLoginHandlers(() => {
            const buyerLoginId = getBuyerId(document.cookie);
            if (buyerLoginId) {
                refetch({ userId: buyerId, fetchUser: true });
            }
        });
    }, [refetch]);

    return (
        <div className={styles.container}>
            <div
                className={classnames(styles.topContent, {
                    [styles.hasCreateProfile]: currentUserId && currentUserId === userId,
                })}
            >
                <NftPublicProfileAddressBadge walletAddress={walletAddress} ensDomain={ensDomain} />
                {currentUserId && currentUserId === userId ? (
                    <Button
                        href="/profile-edit"
                        type={isMobile ? 'primary' : 'secondary'}
                        size={isMobile ? 'medium' : 'small'}
                        className={classnames({ [styles.floatingButton]: isMobile })}
                    >
                        <FormattedMessage
                            id="nft.profileAddressLayout.createProfileCta"
                            defaultMessage="Create Profile"
                        />
                    </Button>
                ) : null}
            </div>

            {viewer && (
                <NftProfileCollection
                    viewer={viewer}
                    userHasMinterRole={userHasMinterRole}
                    userIdentifiyingText={shortAddress(walletAddress)}
                    profileUserId={userId}
                    profileWalletAddress={walletAddress}
                />
            )}
        </div>
    );
};
